import React from "react";
import {
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaCalendarAlt,
} from "react-icons/fa";
import { GiViolin, GiMusicalScore } from "react-icons/gi";
import { useForm } from "@formspree/react";

/* ------------------------------------------------------------------ */
/*  Dati                                                               */
/* ------------------------------------------------------------------ */
const INDIRIZZO = "Via Don Bosco 10, Milano";
const linkMappa = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
  `Parrocchia di S. Luigi Gonzaga, ${INDIRIZZO}`,
)}`;

const contatti = [
  {
    icon: <FaEnvelope />,
    label: "milarte.mi@gmail.com",
    href: "mailto:milarte.mi@gmail.com",
  },
  {
    icon: <FaEnvelope />,
    label: "milarte.mi@pec.it",
    href: "mailto:milarte.mi@pec.it",
  },
  {
    icon: <FaPhoneAlt />,
    label: "+39 338 830 3778",
    href: "tel:+393388303778",
  },
  {
    icon: <GiMusicalScore />,
    label: "Giuseppe Dinardo",
    href: "https://www.giuseppedinardo.it/",
    external: true,
  },
];

const inputClass =
  "mt-1 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400";

/* ------------------------------------------------------------------ */
/*  Pagina                                                             */
/* ------------------------------------------------------------------ */
const SuonaConNoi = () => {
  const [state, handleSubmit] = useForm("xnpnpqev");

  return (
    <section
      id="suona-con-noi"
      className="relative flex flex-col items-center w-full min-h-screen pb-20 bg-gradient-to-br from-white via-amber-50 to-amber-100 overflow-hidden"
    >
      <GiViolin className="fixed text-amber-200/40 text-[180px] rotate-12 -top-5 -right-5 z-0" />

      <div className="relative z-10 w-11/12 max-w-6xl mt-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        {/* --- COLONNA SINISTRA: INFORMAZIONI --- */}
        <div>
          <h1 className="text-red-600 font-extrabold text-4xl md:text-5xl tracking-wide">
            Suona con noi
          </h1>
          <p className="mt-5 text-2xl md:text-3xl font-semibold text-amber-900 leading-snug">
            Vieni a far parte della <strong>Nuova Orchestra di Milano</strong>!
          </p>

          {/* Prove */}
          <div className="mt-8 flex flex-col gap-5">
            <div className="flex items-start gap-4">
              <span className="shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-red-50 text-red-600 text-xl">
                <FaCalendarAlt />
              </span>
              <p className="text-lg md:text-xl text-gray-800 leading-relaxed">
                Le prove si tengono ogni{" "}
                <span className="font-semibold">mercoledì sera</span>
              </p>
            </div>

            <div className="flex items-start gap-4">
              <span className="shrink-0 flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 text-amber-700 text-xl">
                <FaMapMarkerAlt />
              </span>
              <p className="text-lg md:text-xl text-gray-800 leading-relaxed">
                presso la <strong>Parrocchia di S. Luigi Gonzaga</strong> a
                Milano,
                <br />
                <a
                  href={linkMappa}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-600 font-semibold underline underline-offset-4 hover:text-red-500"
                >
                  Via Don Bosco 10
                </a>
              </p>
            </div>
          </div>

          {/* Contatti */}
          <div className="mt-10 bg-amber-100/70 border border-amber-200 rounded-3xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-gray-900">
              Per informazioni
            </h2>
            <ul className="mt-4 flex flex-col gap-3 text-base md:text-lg">
              {contatti.map((c) => (
                <li key={c.label}>
                  <a
                    href={c.href}
                    {...(c.external
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                    className="inline-flex items-center gap-3 text-red-700 font-medium hover:text-red-500 underline-offset-4 hover:underline transition"
                  >
                    <span className="text-red-600">{c.icon}</span>
                    {c.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* --- COLONNA DESTRA: FORM --- */}
        <div className="flex flex-col items-center text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-red-600 mb-6">
            Unisciti alla Nuova Orchestra di Milano
          </h2>

          {state.succeeded ? (
            <p className="text-green-600 text-lg font-semibold">
              ✅ Grazie per averci scritto! Ti contatteremo presto.
            </p>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full bg-white p-6 md:p-8 rounded-3xl shadow-lg text-left"
            >
              <input
                type="hidden"
                name="_subject"
                value="Nuova richiesta Suona con noi - Nuova Orchestra di Milano"
              />

              {/* --- NOME E COGNOME --- */}
              <div className="flex flex-col sm:col-span-2">
                <label
                  htmlFor="nome-cognome"
                  className="text-sm font-medium text-gray-700"
                >
                  Nome e Cognome<span className="text-red-600">*</span>
                </label>
                <input
                  id="nome-cognome"
                  name="Nome e Cognome"
                  type="text"
                  required
                  placeholder="Es. Mario Rossi"
                  className={inputClass}
                />
              </div>

              {/* --- TELEFONO --- */}
              <div className="flex flex-col">
                <label
                  htmlFor="telefono"
                  className="text-sm font-medium text-gray-700"
                >
                  Numero di Telefono<span className="text-red-600">*</span>
                </label>
                <input
                  id="telefono"
                  name="Numero di Telefono"
                  type="tel"
                  required
                  placeholder="Es. +39 333 1234567"
                  className={inputClass}
                />
              </div>

              {/* --- EMAIL --- */}
              <div className="flex flex-col">
                <label
                  htmlFor="email"
                  className="text-sm font-medium text-gray-700"
                >
                  Email<span className="text-red-600">*</span>
                </label>
                <input
                  id="email"
                  name="Email"
                  type="email"
                  required
                  placeholder="nome@email.it"
                  className={inputClass}
                />
              </div>

              {/* --- NOTE --- */}
              <div className="flex flex-col sm:col-span-2">
                <label
                  htmlFor="note"
                  className="text-sm font-medium text-gray-700"
                >
                  Note aggiuntive
                </label>
                <textarea
                  id="note"
                  name="Note"
                  rows="4"
                  placeholder="Es. Strumento che suoni, livello, disponibilità..."
                  className={inputClass}
                />
              </div>

              {/* --- BOTTONE --- */}
              <div className="col-span-full flex justify-center mt-4">
                <button
                  type="submit"
                  disabled={state.submitting}
                  className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition disabled:opacity-50 font-semibold"
                >
                  Invia richiesta
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default SuonaConNoi;